'use client';

import { FileText } from 'lucide-react';

interface LoggingCompleteModalProps {
  onFinish: () => void;
}

export function LoggingCompleteModal({ onFinish }: LoggingCompleteModalProps) {
  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Logging tour complete"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Icon */}
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent-blue/[0.08]">
          <FileText className="h-8 w-8 text-accent-blue" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-900">
          Every Conversation Documented
        </h2>

        {/* Content */}
        <div className="text-left space-y-4 mb-8">
          <p className="text-base leading-relaxed text-gray-500">
            With Voley&rsquo;s smart-logging, your agency never has to worry about missing a
            conversation again. Between manual logging, log-on-close prompts, and automatic
            end-of-day backups, <strong className="text-gray-700">every client interaction is
            captured</strong>.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-900">Pro tip:</strong> Enable both &ldquo;Log on
              Close&rdquo; and &ldquo;End-of-Day&rdquo; logging for maximum coverage. Your team
              gets prompted to log when closing conversations, and anything they miss gets caught
              automatically at the end of the day.
            </p>
          </div>
          <p className="text-sm leading-relaxed text-gray-500">
            Every log is easy to find in your AMS client profile &mdash; searchable, timestamped,
            and organized. You can also view your complete logging history in Settings.
          </p>
        </div>

        {/* Finish button */}
        <div className="flex justify-center">
          <button
            onClick={onFinish}
            aria-label="Finish tour"
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-base font-semibold text-white bg-accent-blue hover:brightness-110 transition-all shadow-md"
          >
            Finish Tour
          </button>
        </div>
      </div>
    </div>
  );
}
